<template>
    <div class="info-root">
        <div class="info-top info-box">
            <div class="info-title">
                <template v-if="!isLoadingConfiguration">
                    <div class="w-100">
                        <info-title class="title-text">
                            {{configuration.title}}
                        </info-title>

                        <a class="btn btn-outline-secondary btn-xs" :href="navigateToSelectedBrickUrl">
                            {{resource('JourneyBuilder_TouchpointInfo_ChangeTouchpoint')}}
                        </a>
                    </div>

                    <info-sub-title>
                        {{configuration.subTitle}}
                    </info-sub-title>
                </template>
            </div>
            
            <div class="info-touchpoint-selectors">
                <v-select class="form-control"
                    :placeholder="resource('JourneyBuilder_TouchpointInfo_PreviousTouchpoint')"
                    :options="previousTouchPoints"
                    :value="null"
                    :disabled="previousTouchPoints.length === 0"

                    @input="selectTouchPoint"
                />
                
                <v-select class="form-control"
                    :placeholder="resource('JourneyBuilder_TouchpointInfo_NextTouchpoint')"
                    :options="nextTouchPoints"
                    :value="null"
                    :disabled="nextTouchPoints.length === 0"


                    @input="selectTouchPoint"
                />
            </div>

            <div class="info-close-button">
                <button class="close"
                    type="button"
                    @click.prevent="close"
                ><span aria-hidden="true">&times;</span></button>
            </div>
            
            <ul class="info-nav-tabs">
                <li v-if="!isLoadingConfiguration"
                    v-for="tab in tabs"
                    :key="tab.title"
                    class="info-nav-tab"
                    :class="{ active: tab === activeTab }"
                    
                    @click="selectedTab = tab"
                >
                    {{tab.title}}
                </li>
            </ul>
            
            <div class="info-dashboard-navigation-button">
                <a class="btn btn-outline-secondary btn-xs" :class="{ disabled: !navigateToDashboardUrl }" :href="navigateToDashboardUrl || '#'">
                    {{resource('JourneyBuilder_Sankey_TouchpointInfo_OpenDashboard_Button')}}
                </a>
            </div>
        </div>
        
        <div class="info-bottom info-box" ref="widget-container">
            <div class="info-controls">
                <template v-if="!isLoadingPeriodRange">
                    <info-sub-title v-if="startDate && endDate">
                        {{startDate | displayDate}} t/m {{endDate | displayDate}}
                    </info-sub-title>
                </template>
                <loader v-else />
                
                <div class="info-buttons">
                    <button 
                        v-if="detailsButtonTarget" 
                        @click="scrollToDetailsTarget"
                        class="btn btn-outline-secondary"
                    >{{resource('JourneyBuilder_Sankey_TouchpointInfo_Details_Button')}}</button>
                    
                    <export-tab-button
                        v-if="activeTab"
                        :tabId="activeTab.id" 
                        :brick-id="selectedBrickId" 
                    />
                </div>
            </div>

            <template v-if="!isLoadingConfiguration">
                <keep-alive v-if="activeTab">
                    <widgets-wrapper :key="activeTab.title" class="info-widgets">
                        <widget v-for="widgetConfiguration in widgets"
                                :key="widgetConfiguration.id"
                                :id="formatWidgetDOMId(widgetConfiguration.id)"
                                class="info-widget"
                                :configuration="widgetConfiguration"
                                :brick-id="selectedBrickId"
                        />
                    </widgets-wrapper>
                </keep-alive>
                <label v-else>{{resource('Chart_ErrorMessage_NoData')}}</label>
            </template>
            <loader v-else />
        </div>
    </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';

import api from "@modules/JourneyBuilder/api";
import widgetTypes from './widget-types';

import {debounceWithLeadingCallAsync, supportsStatistics} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/helpers";
import {bootstrapDangerGrowl} from "@core/Util/growler";
import {appendFiltersToUrl} from "@core/Util/dashboard-filter";

import resourceMixin from "@core/Util/Mixins/resources";

import VSelect from 'vue-select';

import InfoSubTitle from "./InfoSubTitle";
import InfoTitle from "./InfoTitle";
import Widget from "./Widget";
import ExportTabButton from "./ExportTabButton";
import Loader from "@modules/JourneyBuilder/Components/Elements/Loader";

const {mapState, mapGetters, mapMutations} = createNamespacedHelpers('journeyBuilder/reporting');

//Vue's keep-alive only works on components
//therefor we need to create a special 'wrapper' component as to not re-create
//all our widgets each tab switch
const WidgetsWrapper = {
    render(createElement) {
        return createElement('div', this.$slots.default);
    }
}

export default {
    name: "TouchpointInfo",
    components: {
        Loader,
        VSelect,
        
        InfoSubTitle,
        InfoTitle,
        ExportTabButton,
        Widget,
        WidgetsWrapper
    },
    mixins: [resourceMixin],
    async created() {
        this.loadConfiguration = debounceWithLeadingCallAsync(this.loadConfiguration).bind(this);
        
        await this.loadConfiguration();
    },
    methods: {
        ...mapMutations(['setSelectedBrickId']),
        async loadConfiguration(){
            this.isLoadingConfiguration = true;
            
            //Allow our UI to update
            await this.$nextTick();
            
            //We should be fine just leaving the UI in a loading marker limbo on error
            //on error, we display an error message to the user, we should not revert back to our previous brick
            try {
                this.$set(this, 'configuration', await api.getBrickStatisticsConfiguration(this.selectedBrickId));
                this.isLoadingConfiguration = false;
            } catch(e){
                bootstrapDangerGrowl(e);
            }
        },
        selectTouchPoint(touchPoint){
            this.setSelectedBrickId(touchPoint.brickId);
        },
        close(){
            this.$emit('close');
        },
        formatWidgetDOMId(widgetId){
            return `widget_${widgetId}`;
        },
        scrollToDetailsTarget(){
            const MARGIN_TOP = 5;
            
            const detailsTarget = this.$el.querySelector(`#${this.detailsButtonTarget}`);
            if(!detailsTarget){
                return;
            }
            
            const { top } = detailsTarget.getBoundingClientRect();

            this.$refs['widget-container'].scrollTo({
                top: top + detailsTarget.clientHeight - MARGIN_TOP
            });
        },
        
        /** 
         * Find all previous/next touchpoints of the selected touchpoints
         * 
         * @param { "next" | "previous" } direction 
         * @returns {SankeyNode[]}
         * */
        getConnectedTouchPoints(direction) {
            /*
                Next = all touchpoints with 'from' equal to current selection node 'id' properties
                Previous = all touchpoints with node 'id' equal to current selection 'from' properties
            */
            
            const sourceIdProperty = direction  === 'next' ? 'id' : 'from';
            const targetIdProperty = direction  === 'next' ? 'from' : 'id';
            
            const selectedTouchPointIds = this.selectedTouchPoints.map(tp => tp[sourceIdProperty]);
            const includedBrickIds = [];

            return this.touchPoints.filter(tp => {
                //Only include touchPoints with statistics
                if(!supportsStatistics(tp)){
                    return false;
                }
                
                //Each brick should only be included once - a single brick may have multiple touchpoints
                if(includedBrickIds.includes(tp.brickId)) {
                    return false;
                }

                if(selectedTouchPointIds.includes(tp[targetIdProperty])) {
                    includedBrickIds.push(tp.brickId);
                    return true;
                }
                
                return false;
            })
            //Sort alphabetically    
            .sort(({label: left}, {label: right}) => left.localeCompare(right, { sensitivity: 'base' }));
        }
    },
    filters: {
        displayDate(dateString){
            //DateString is assumed to be parseable by moment
            return moment(dateString).format('DD-MM-YYYY');
        }
    },
    watch: {
        async 'selectedBrickId'(){
            await this.loadConfiguration();
        },
        tabs(newValue){
            //When a new set of tabs is provided; match tabs by name
            //and attempt to preselect the current active tab
            if(!this.selectedTab) {
                this.selectedTab = undefined;
                return;
            }
            
            this.selectedTab = newValue.find(t => t.id === this.selectedTab.id);
        }
    },
    computed: {
        ...mapState({
            'isLoadingPeriodRange': 'isLoadingPeriodRange',
            'startDate': 'periodStart',
            'endDate': 'periodEnd',
            'productNames': 'productNames',
            'touchPoints': 'sankeyData',
            'selectedBrickId': 'selectedBrickId'
        }),
        ...mapGetters({
            'selectedTouchPoints': 'selectedNodes'
        }),
        tabs() {
            if(this.isLoadingConfiguration){
                return [];
            }
            
            return this.configuration.tabs;
        },
        widgets(){
            return (this.activeTab && this.activeTab.widgets) || [];
        },
        activeTab(){
            //Take either the selected, or the first available tab
            if(this.selectedTab){
                return this.selectedTab;
            }

            return this.tabs && this.tabs[0];
        },
        detailsButtonTarget(){
            //Our navigate to details button should be displayed when:
            // * Our first widget is a bar
            // * Our tab contains a table
            //the table widget is assumed to contain 'details'
            
            //Requested to be disabled
            return null;
            
            /**
             * @type SankeyTouchpointInfoConfigWidget[]
             */
            const widgets = this.widgets;
            
            if(widgets.length < 2){
                return null;
            }
            
            const barWidget = widgets[0];
            const tableWidget = widgets.find(w => w.type === widgetTypes.DETAILS_TABLE);
            
            if(tableWidget && barWidget.type === widgetTypes.BAR){
                return this.formatWidgetDOMId(tableWidget.id);
            }
            
            return null;
        },
        nextTouchPoints(){
            return this.getConnectedTouchPoints('next');
        },
        previousTouchPoints(){
            return this.getConnectedTouchPoints('previous');
        },
        navigateToSelectedBrickUrl(){
            return api.getViewJourneyBrickUrl(this.selectedBrickId);
        },
        navigateToDashboardUrl() {
            if(this.isLoadingConfiguration || !this.configuration.dashboard.dashboardId) {
                return undefined;
            }
            
            /** @type { Array.<{ filterId: String, values: String|String[] }> } */
            const filters = [];
            
            const addFilter = (filterId, values) => {
                //Ignore filters without a filterId/value
                if(
                    !filterId ||
                    !values || 
                    (Array.isArray(values) && !values.length)
                ) {
                    return;
                }
                
                filters.push({
                    filterId,
                    values
                });
            };
            
            addFilter('startDate', this.startDate);
            addFilter('endDate', this.endDate);
            addFilter(this.configuration.dashboard.productFilterId, this.productNames);
            addFilter(this.configuration.dashboard.touchpointFilterId, this.selectedBrickId);

            return appendFiltersToUrl(
                `/dashboard/index/${this.configuration.dashboard.dashboardId}`,
                filters
            );
        }
    },
    data: () => ({
        selectedTab: undefined,

        /**
         * @type {SankeyTouchpointInfoConfig}
         */
        configuration: undefined,

        /**
         * @type {Boolean}
         */
        isLoadingConfiguration: true
    })
}
</script>

<style scoped lang="scss">
    $box-spacing: 20px;
    
    .info-box {
        background-color: #fff;
        box-shadow: 0 0 6px 3px #DADADA;
        padding: 5px 5px 5px 15px;

        &.info-top {
            display: grid;
            row-gap: 10px;
            grid-template-columns: 0.5fr 0.3fr 0.1fr 0.1fr;
            grid-template-areas: 
                "title touchpoint-selectors touchpoint-selectors close-button"
                "nav-tabs nav-tabs dashboard-navigation-button dashboard-navigation-button";
            
            margin-bottom: $box-spacing / 2;
        }

        &.info-bottom {
            height: 100%;
            margin-top: $box-spacing / 2;
            
            overflow-y: scroll;
        }
    }
    
    .info-title {
        grid-area: title;
        
        .title-text {
            display: inline-block;

            //Ensure spacing between 'change touchpoint' button
            margin-right: 3rem;
            margin-bottom: 0.25rem;

            //https://developer.mozilla.org/en-US/docs/Web/CSS/word-break#values
            //Usage of word-break: break-word is discouraged by MDN
            //Ensure our text never goes beyond the boundaries defined by its container
            word-break: normal;
            overflow-wrap: anywhere;
        }
    }

    .info-touchpoint-selectors {
        grid-area: touchpoint-selectors;

        height: 34px; //.form-control default
        margin-left: 5px;
        
        display: flex;
        column-gap: 5px;
    }
    
    .info-close-button {
        grid-area: close-button;
        
        //Make our button more easily clickable
        button {
            padding: 5px;
        }
    }
    
    .info-nav-tabs {
        grid-area: nav-tabs;
        
        padding-left: 0;
        margin-bottom: 5px;
        
        .info-nav-tab {
            list-style: none;
            display: inline-block;
            background-color: #EFEFEF;

            border-color: #D0D0D0;
            border-width: 1px;
            border-style: solid;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;

            padding: 5px 15px;

            &.active {
                background-color: #FFFFFF;
            }
            
            &:hover {
                cursor: pointer;
            }
        }
    }
    
    .info-dashboard-navigation-button {
        grid-area: dashboard-navigation-button;
        text-align: right;
    }
    
    .info-controls {
        //Prevent the bottom from our export button from becoming unclickable
        position: relative;
        z-index: 1;
        
        display: flex;
        flex-direction: column;
        float: right;
        
        &:not(:first-child){
            margin-top: 5px;
        }
    }
    
    .info-buttons {
        text-align: right;
    }
    
    .info-widget:not(:last-child) {
        margin-bottom: 15px;
    }
</style>